import React from 'react'
import {
    Box,
    Container,
    Grid,
    Typography,
    Link,
    Divider,
    IconButton,
    useTheme,
    Collapse,
    List,
    ListItem,
    ListItemButton,
    ListItemText
} from '@mui/material'
import {
    Facebook,
    Instagram,
    YouTube,
    Pinterest,
    Email,
    Phone,
    LocationOn,
    ExpandLess,
    ExpandMore
} from '@mui/icons-material'
import { useLanguage } from '../context/LanguageContext'

const Footer = () => {
    const theme = useTheme()
    const { t } = useLanguage()
    const [openCourses, setOpenCourses] = React.useState(false)

    const toggleCoursesMenu = () => {
        setOpenCourses(!openCourses)
    }

    return (
        <Box
            component="footer"
            sx={{
                backgroundColor: '#1a1a1a',
                color: 'white',
                py: 6,
                borderTop: `1px solid ${theme.palette.divider}`
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Company Info */}
                    <Grid item xs={12} md={4}>
                        <Typography
                            variant="h5"
                            component="div"
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                color: theme.palette.primary.dark,
                                fontFamily: theme.typography.fontFamily
                            }}
                        >
                            {t('footer.companyName')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            {t('footer.companyDescription')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            {[
                                {
                                    Icon: Facebook,
                                    link: 'https://www.facebook.com/profile.php?id=61576890105877',
                                    ariaLabel: 'Facebook'
                                },
                                {
                                    Icon: Instagram,
                                    link: '#',
                                    ariaLabel: 'Instagram'
                                },
                                {
                                    Icon: YouTube,
                                    link: '#',
                                    ariaLabel: 'YouTube'
                                },
                            ].map(({ Icon, link, ariaLabel }, index) => (
                                <Link
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    key={index}
                                    sx={{ textDecoration: 'none' }}
                                >
                                    <IconButton
                                        aria-label={ariaLabel}
                                        sx={{
                                            color: theme.palette.primary.light,
                                            backgroundColor: `${theme.palette.primary.main}08`,
                                            '&:hover': {
                                                backgroundColor: theme.palette.primary.main,
                                                color: theme.palette.common.white
                                            },
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <Icon />
                                    </IconButton>
                                </Link>
                            ))}
                        </Box>
                    </Grid>

                    {/* Quick Links */}
                    <Grid item xs={6} md={2}>
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 2,
                                color: theme.palette.primary.dark,
                                fontFamily: theme.typography.fontFamily
                            }}
                        >
                            {t('footer.quickLinks')}
                        </Typography>
                        <Box component="ul" sx={{ listStyle: 'none', p: 0 }}>
                            <li>
                                <Link
                                    href="/home"
                                    underline="hover"
                                    color="text.primary"
                                    sx={{
                                        display: 'block',
                                        mb: 1,
                                        color: 'white',
                                        '&:hover': {
                                            color: theme.palette.primary.main
                                        }
                                    }}
                                >
                                    {t('footer.home')}
                                </Link>
                            </li>
                            <li>
                                <ListItemButton
                                    onClick={toggleCoursesMenu}
                                    sx={{
                                        p: 0,
                                        mb: 1,
                                        color: 'white',
                                        '&:hover': {
                                            color: theme.palette.primary.main
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={t('footer.courses')}
                                        sx={{ my: 0 }}
                                    />
                                    {openCourses ? <ExpandLess sx={{ fontSize: '1rem' }} /> : <ExpandMore sx={{ fontSize: '1rem' }} />}
                                </ListItemButton>
                                <Collapse in={openCourses} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        <ListItem disablePadding>
                                            <Link
                                                href="/business-management"
                                                underline="hover"
                                                color="text.primary"
                                                sx={{
                                                    display: 'block',
                                                    mb: 1,
                                                    pl: 2,
                                                    color: 'white',
                                                    '&:hover': {
                                                        color: theme.palette.primary.main
                                                    }
                                                }}
                                            >
                                                {t('businessManagment')}
                                            </Link>
                                        </ListItem>
                                        <ListItem disablePadding>
                                            <Link
                                                href="/skillset-course"
                                                underline="hover"
                                                color="text.primary"
                                                sx={{
                                                    display: 'block',
                                                    mb: 1,
                                                    pl: 2,
                                                    color: 'white',
                                                    '&:hover': {
                                                        color: theme.palette.primary.main
                                                    }
                                                }}
                                            >
                                                {t('skillsetCourse')}
                                            </Link>
                                        </ListItem>
                                    </List>
                                </Collapse>
                            </li>
                            <li>
                                <Link
                                    href="/package"
                                    underline="hover"
                                    color="text.primary"
                                    sx={{
                                        display: 'block',
                                        mb: 1,
                                        color: 'white',
                                        '&:hover': {
                                            color: theme.palette.primary.main
                                        }
                                    }}
                                >
                                    {t('footer.membership')}
                                </Link>
                            </li>
                        </Box>
                    </Grid>

                    {/* Resources */}
                    <Grid item xs={6} md={2}>
                        <Typography
                            variant="h6"
                            sx={{
                                mb: 2,
                                color: theme.palette.primary.dark,
                                fontFamily: theme.typography.fontFamily
                            }}
                        >
                            {t('footer.resources')}
                        </Typography>
                        <Box component="ul" sx={{ listStyle: 'none', p: 0 }}>
                            {[
                                { text: t('footer.faq'), href: '#' },
                                { text: t('footer.support'), href: '#' },
                                { text: t('footer.careers'), href: '#' }
                            ].map((item, index) => (
                                <li key={index}>
                                    <Link
                                        href={item.href}
                                        underline="hover"
                                        color="text.primary"
                                        sx={{
                                            display: 'block',
                                            color: 'white',
                                            mb: 1,
                                            '&:hover': {
                                                color: theme.palette.primary.main
                                            }
                                        }}
                                    >
                                        {item.text}
                                    </Link>
                                </li>
                            ))}
                        </Box>
                    </Grid>

                    {/* Contact Info - These remain the same across languages */}
                    <Grid item xs={12} md={4}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Email sx={{
                                mr: 1,
                                color: theme.palette.primary.main,
                                fontSize: '1.2rem'
                            }} />
                            <Typography variant="body2">
                                beautyeduonline@gmail.com
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Phone sx={{
                                mr: 1,
                                color: theme.palette.primary.main,
                                fontSize: '1.2rem'
                            }} />
                            <Typography variant="body2">+6016-267 7356</Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Divider sx={{
                    my: 4,
                    borderColor: theme.palette.divider,
                    opacity: 0.5
                }} />

                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <Typography variant="body2">
                        © {new Date().getFullYear()} {t('footer.companyName')}. {t('footer.copyright')}.
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 1
                    }}>
                        {[
                            t('footer.privacyPolicy'),
                            t('footer.terms'),
                            t('footer.accessibility')
                        ].map((text, index) => (
                            <Link
                                key={index}
                                href="#"
                                underline="hover"
                                sx={{
                                    color: 'white',
                                    '&:hover': {
                                        color: theme.palette.primary.main
                                    }
                                }}
                            >
                                {text}
                            </Link>
                        ))}
                    </Box>
                </Box>
            </Container>
        </Box>
    )
}

export default Footer